import { historicalProductName, historicalSku, historicalVariantOptions, hasLiveCatalogueIdentity, type CatalogueHistoryFields } from "@/lib/catalogue-history";
import { Fragment, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { firstRelation } from "@/lib/relations";
import { useAuth } from "@/context/AuthContext";
import { canManagePurchaseOrders, canReceivePurchaseOrders } from "@/lib/permissions";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { ListSkeleton } from "@/components/base/ListSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { ConfirmDialog } from "@/components/base/ConfirmDialog";
import { money, fmtDate, variantLabel } from "@/lib/format";
import { getActiveCurrencyCode } from "@/lib/currency";
import { validateOrderQuantity, currenciesDiffer, resolvePoCurrency, poStatusLabel, canApprovePo, canReceivePo, canCancelPo, computePoProgress, type SupplierOrderRule } from "@/lib/purchasing";
import * as purchasingApi from "@/lib/api/purchasing";
import { downloadCsv, toCsv } from "@/lib/csv";
import Pagination from "@/components/base/Pagination";
import { useCreateDeepLink } from "@/hooks/useCreateDeepLink";
import { effectiveCostPrice } from "@/lib/products";

interface PoRow {
  id: string;
  po_number: string;
  supplier_id: string;
  location_id: string;
  status: string;
  order_date: string | null;
  expected_date: string | null;
  total: number;
  suppliers?: { name: string } | null;
  locations?: { name: string } | null;
}

interface PoItem extends CatalogueHistoryFields {
  id: string;
  purchase_order_id: string;
  product_id: string | null;
  variant_id: string | null;
  qty_ordered: number;
  qty_received: number;
  unit_cost: number;
  products?: { name: string; sku: string } | null;
  variants?: { sku: string | null; option_values: Record<string, string> | null } | null;
}

interface Option { id: string; name: string; lead_time_days?: number | null; currency?: string | null; }
interface ProductOption { id: string; name: string; sku: string; cost_price: number | null; }
interface SupplierProductRow { supplier_id: string; product_id: string; min_order_qty: number | null; order_multiple: number | null; supplier_price: number | null; supplier_sku: string | null; currency: string | null; }

interface DraftLine { uid: string; product_id: string; variant_id: string | null; qty: number; unit_cost: string; supplier_sku: string; }
interface VariantOption { id: string; product_id: string; sku: string | null; option_values: Record<string, string> | null; cost_price: number | null; }

const sanitize = (q: string) => q.replace(/[,%_()]/g, "").trim();

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

export default function PurchaseOrders() {
  const { org, role } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [receivePo, setReceivePo] = useState<PoRow | null>(null);
  const [cancelPo, setCancelPo] = useState<PoRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // create form
  const [supplierId, setSupplierId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [createIdemKey, setCreateIdemKey] = useState("");
  const [notes, setNotes] = useState("");
  const [supplierReference, setSupplierReference] = useState("");
  const [shippingCost, setShippingCost] = useState("");
  const [discount, setDiscount] = useState("");
  const [poCurrency, setPoCurrency] = useState("");

  // receive form
  const [receiveQty, setReceiveQty] = useState<Record<string, number>>({});
  const [receiveIdemKey, setReceiveIdemKey] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["purchase-orders", org?.id, search, statusFilter, page, pageSize],
    enabled: !!org?.id,
    queryFn: async () => {
      let q = supabase
        .from("purchase_orders")
        .select("id, po_number, supplier_id, location_id, status, order_date, expected_date, total, suppliers(name), locations(name)", { count: "exact" })
        .eq("org_id", org!.id);
      const term = sanitize(search);
      if (term) {
        const ilike = `%${term}%`;
        const { data: matchedSuppliers } = await supabase
          .from("suppliers")
          .select("id")
          .eq("org_id", org!.id)
          .ilike("name", ilike)
          .limit(100);
        const supplierIds = ((matchedSuppliers ?? []) as { id: string }[]).map((s) => s.id);
        if (supplierIds.length) {
          q = q.or(`po_number.ilike.${ilike},supplier_id.in.(${supplierIds.join(",")})`);
        } else {
          q = q.ilike("po_number", ilike);
        }
      }
      if (statusFilter) q = q.eq("status", statusFilter);
      q = q.order("created_at", { ascending: false }).range(page * pageSize, page * pageSize + pageSize - 1);
      const posRes = await q;
      const pos = (posRes.data ?? []).map((row) => ({
        ...row,
        suppliers: firstRelation(row.suppliers),
        locations: firstRelation(row.locations),
      })) as PoRow[];
      const poIds = pos.map((p) => p.id);
      const [items, suppliers, locations, products, supplierProducts, variants] = await Promise.all([
        poIds.length
          ? supabase.from("purchase_order_items").select("catalogue_product_identity_id, catalogue_variant_identity_id, product_name_snapshot, sku_snapshot, variant_attributes_snapshot, catalogue_snapshot_origin, id, purchase_order_id, product_id, variant_id, qty_ordered, qty_received, unit_cost, products(name, sku), variants(sku, option_values)").eq("org_id", org!.id).in("purchase_order_id", poIds)
          : Promise.resolve({ data: [] }),
        supabase.from("suppliers").select("id, name, lead_time_days, currency").eq("org_id", org!.id).order("name"),
        supabase.from("locations").select("id, name").eq("org_id", org!.id).order("name"),
        supabase.from("products").select("id, name, sku, cost_price").eq("org_id", org!.id).neq("status", "archived").order("name"),
        supabase.from("supplier_products").select("supplier_id, product_id, min_order_qty, order_multiple, supplier_price, supplier_sku, currency").eq("org_id", org!.id),
        supabase.from("variants").select("id, product_id, sku, option_values, cost_price").eq("org_id", org!.id).neq("status", "archived").order("position"),
      ]);
      return {
        pos,
        total: posRes.count ?? 0,
        items: (items.data ?? []) as PoItem[],
        suppliers: (suppliers.data ?? []) as Option[],
        locations: (locations.data ?? []) as Option[],
        products: (products.data ?? []) as ProductOption[],
        supplierProducts: (supplierProducts.data ?? []) as SupplierProductRow[],
        variants: (variants.data ?? []) as VariantOption[],
      };
    },
  });

  const itemsByPo = (data?.items ?? []).reduce<Record<string, PoItem[]>>((acc, it) => {
    (acc[it.purchase_order_id] = acc[it.purchase_order_id] ?? []).push(it);
    return acc;
  }, {});

  const addLine = () => setLines((l) => [...l, { uid: crypto.randomUUID(), product_id: "", variant_id: null, qty: 1, unit_cost: "", supplier_sku: "" }]);

  const resolveCostForSupplier = (sid: string, productId: string, variantId: string | null = null): number => {
    const prod = data?.products.find((p) => p.id === productId);
    const supplierLink = data?.supplierProducts.find((sp) => sp.supplier_id === sid && sp.product_id === productId);
    if (supplierLink?.supplier_price != null) return Number(supplierLink.supplier_price);
    const productVariants = (data?.variants ?? []).filter((v) => v.product_id === productId);
    const variant = variantId
      ? productVariants.find((v) => v.id === variantId)
      : productVariants.length === 1
        ? productVariants[0]
        : undefined;
    return effectiveCostPrice(variant?.cost_price ?? null, prod?.cost_price ?? null) ?? 0;
  };

  const resolveSupplierSkuForSupplier = (sid: string, productId: string): string => {
    const supplierLink = data?.supplierProducts.find((sp) => sp.supplier_id === sid && sp.product_id === productId);
    return supplierLink?.supplier_sku ?? "";
  };

  const resolveOrderRuleForSupplier = (sid: string, productId: string): SupplierOrderRule => {
    const supplierLink = data?.supplierProducts.find((sp) => sp.supplier_id === sid && sp.product_id === productId);
    return {
      minOrderQty: supplierLink?.min_order_qty ?? null,
      orderMultiple: supplierLink?.order_multiple ?? null,
    };
  };

  const resolveLineCurrency = (sid: string, productId: string): string | null => {
    const supplierLink = data?.supplierProducts.find((sp) => sp.supplier_id === sid && sp.product_id === productId);
    return supplierLink?.currency ?? null;
  };

  const orderRuleHint = (sid: string, productId: string): string => {
    const supplierLink = data?.supplierProducts.find((sp) => sp.supplier_id === sid && sp.product_id === productId);
    const moq = supplierLink?.min_order_qty ?? 0;
    const multiple = supplierLink?.order_multiple ?? 0;
    const parts: string[] = [];
    if (moq > 1) parts.push(`minimum ${moq}`);
    if (multiple > 1) parts.push(`multiples of ${multiple}`);
    return parts.length ? `Supplier order rule: ${parts.join(", ")}` : "";
  };

  const resolveCost = (productId: string, variantId: string | null = null): number =>
    resolveCostForSupplier(supplierId, productId, variantId);

  const handleSelectProduct = (idx: number, productId: string) => {
    const vs = (data?.variants ?? []).filter((v) => v.product_id === productId);
    const variantId = vs.length === 1 ? vs[0].id : null;
    updateLine(idx, { product_id: productId, variant_id: variantId, unit_cost: String(resolveCost(productId, variantId)), supplier_sku: resolveSupplierSkuForSupplier(supplierId, productId) });
  };

  const handleSelectSupplier = (id: string) => {
    setSupplierId(id);
    const supplier = data?.suppliers.find((s) => s.id === id);
    // The PO transacts in the supplier's currency, never silently in the org
    // currency (which could reinterpret a foreign-denominated supplier price).
    setPoCurrency(resolvePoCurrency(null, supplier?.currency, getActiveCurrencyCode()));
    // Re-resolve unit costs using the chosen supplier's negotiated price where available.
    setLines((l) => l.map((line) => (line.product_id ? { ...line, unit_cost: String(resolveCostForSupplier(id, line.product_id, line.variant_id)), supplier_sku: resolveSupplierSkuForSupplier(id, line.product_id) } : line)));
    // Auto-suggest the expected date from the supplier's lead time when not already set.
    if (supplier?.lead_time_days && !expectedDate) {
      const d = new Date();
      d.setDate(d.getDate() + supplier.lead_time_days);
      setExpectedDate(d.toISOString().slice(0, 10));
    }
  };

  const updateLine = (idx: number, patch: Partial<DraftLine>) =>
    setLines((l) => l.map((line, i) => (i === idx ? { ...line, ...patch } : line)));

  const removeLine = (idx: number) => setLines((l) => l.filter((_, i) => i !== idx));

  const createTotal = lines.reduce((sum, l) => sum + (Number(l.qty) || 0) * (Number(l.unit_cost) || 0), 0);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!supplierId || !locationId || lines.length === 0) {
      setError("Select a supplier, location, and at least one line item.");
      return;
    }
    if (lines.some((l) => !l.product_id || Number(l.qty) <= 0)) {
      setError("Every line needs a product and a positive quantity.");
      return;
    }
    for (const l of lines) {
      const rule = resolveOrderRuleForSupplier(supplierId, l.product_id);
      const result = validateOrderQuantity(Number(l.qty), rule);
      if (!result.valid) {
        const productName = data?.products.find((p) => p.id === l.product_id)?.name ?? "this product";
        setError(`${productName}: ${result.reason}`);
        return;
      }
    }
    setSaving(true);
    const idemKey = createIdemKey || crypto.randomUUID();
    setCreateIdemKey(idemKey);

    const pLines = lines.map((l) => ({
      product_id: l.product_id,
      variant_id: l.variant_id,
      qty: Number(l.qty),
      unit_cost: Number(l.unit_cost) || 0,
      supplier_sku: l.supplier_sku || null,
    }));

    let poId: string;
    try {
      poId = await purchasingApi.createPurchaseOrder({
        supplierId,
        locationId,
        expectedDate: expectedDate || null,
        currency: poCurrency || getActiveCurrencyCode(),
        notes: notes || null,
        supplierReference: supplierReference || null,
        shippingCost: Number(shippingCost) || 0,
        discount: Number(discount) || 0,
        lines: pLines,
        idempotencyKey: idemKey,
      });
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }
    setSaving(false);

    setCreateOpen(false);
    setSupplierId("");
    setLocationId("");
    setExpectedDate("");
    setLines([]);
    setCreateIdemKey("");
    setNotes("");
    setSupplierReference("");
    setShippingCost("");
    setDiscount("");
    setPoCurrency("");
    queryClient.invalidateQueries({ queryKey: ["purchase-orders", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["product-detail"] });
    if (poId) navigate(`/purchase-orders/${poId}`);
  };

  const openReceive = (po: PoRow) => {
    setReceivePo(po);
    const items = itemsByPo[po.id] ?? [];
    const initial: Record<string, number> = {};
    items.forEach((it) => {
      const remaining = it.qty_ordered - it.qty_received;
      if (remaining > 0) initial[it.id] = remaining;
    });
    setReceiveQty(initial);
    setReceiveIdemKey(crypto.randomUUID());
    setError("");
  };

  const handleReceive = async () => {
    if (!receivePo) return;
    setError("");
    setSaving(true);
    const items = itemsByPo[receivePo.id] ?? [];
    const selected = items.filter((it) => (receiveQty[it.id] ?? 0) > 0);
    if (selected.some((it) => !hasLiveCatalogueIdentity(it))) {
      setSaving(false);
      setError("A pending line has no live catalogue identity. Reconcile it before receiving stock.");
      return;
    }
    const pLines = selected.filter(hasLiveCatalogueIdentity)
      .map((it) => ({ product_id: it.product_id, variant_id: it.variant_id, qty: receiveQty[it.id] }));
    if (pLines.length === 0) {
      setSaving(false);
      setError("Enter a quantity to receive for at least one line.");
      return;
    }
    try {
      await purchasingApi.receivePurchaseOrder(receivePo.id, pLines, receiveIdemKey || undefined);
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }
    setSaving(false);
    setReceivePo(null);
    queryClient.invalidateQueries({ queryKey: ["purchase-orders", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
    purchasingApi.recomputeIncomingBalances(org!.id).then(() => {
      queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
      queryClient.invalidateQueries({ queryKey: ["product-detail"] });
    });
  };

  const handleCancelPo = async () => {
    if (!cancelPo) return;
    setError("");
    setSaving(true);
    try {
      await purchasingApi.cancelPurchaseOrder(cancelPo.id);
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      setCancelPo(null);
      return;
    }
    setSaving(false);
    setCancelPo(null);
    queryClient.invalidateQueries({ queryKey: ["purchase-orders", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const handleApprove = async (po: PoRow) => {
    setError("");
    setSaving(true);
    try {
      await purchasingApi.approvePurchaseOrder(po.id);
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }
    setSaving(false);
    queryClient.invalidateQueries({ queryKey: ["purchase-orders", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const openCreatePo = () => {
    setError("");
    if (!poCurrency) setPoCurrency(getActiveCurrencyCode());
    if ((data?.locations ?? []).length === 1 && !locationId) {
      setLocationId(data!.locations[0].id);
    }
    setCreateOpen(true);
  };

  useCreateDeepLink(openCreatePo, !!data && canManagePurchaseOrders(role));

  const handleExport = async () => {
    let q = supabase
      .from("purchase_orders")
      .select("id, po_number, supplier_id, location_id, status, order_date, expected_date, total, suppliers(name), locations(name)")
      .eq("org_id", org!.id);
    const term = sanitize(search);
    if (term) {
      const ilike = `%${term}%`;
      const { data: matchedSuppliers } = await supabase
        .from("suppliers")
        .select("id")
        .eq("org_id", org!.id)
        .ilike("name", ilike)
        .limit(100);
      const supplierIds = ((matchedSuppliers ?? []) as { id: string }[]).map((s) => s.id);
      if (supplierIds.length) q = q.or(`po_number.ilike.${ilike},supplier_id.in.(${supplierIds.join(",")})`);
      else q = q.ilike("po_number", ilike);
    }
    if (statusFilter) q = q.eq("status", statusFilter);
    q = q.order("created_at", { ascending: false });
    const { data } = await q;
    const rows = (data ?? []).map((row) => ({
      ...row,
      suppliers: firstRelation(row.suppliers),
      locations: firstRelation(row.locations),
    })) as PoRow[];
    const headers = ["po_number", "supplier", "location", "status", "order_date", "expected_date", "total"];
    downloadCsv(
      `purchase-orders-${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(headers, rows.map((p) => [
        p.po_number,
        p.suppliers?.name ?? "",
        p.locations?.name ?? "",
        p.status,
        p.order_date ?? "",
        p.expected_date ?? "",
        p.total,
      ])),
    );
  };

  if (isLoading) return <ListSkeleton />;

  return (
    <div>
      <PageHeader title="Purchase Orders" subtitle="Manage purchasing and receiving workflow">
        <button onClick={handleExport} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
          <i className="ri-download-2-line" /> Export
        </button>
        {canManagePurchaseOrders(role) && (
          <button onClick={openCreatePo} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
            <i className="ri-add-line" />
            New Purchase Order
          </button>
        )}
      </PageHeader>

      {isError ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load purchase orders.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
        </div>
      ) : !data || data.total === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200">
          <EmptyState
            icon="ri-shopping-cart-line"
            title="No purchase orders yet"
            description="Purchase orders track stock you've ordered from suppliers. Create your first one to start replenishing inventory."
            action={
              canManagePurchaseOrders(role) ? (
                <button onClick={openCreatePo} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
                  <i className="ri-add-line" /> Create your first PO
                </button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                placeholder="Search by PO number or supplier..."
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
              <option value="">All statuses</option>
              {["draft", "pending", "partial", "received", "closed", "cancelled"].map((s) => <option key={s} value={s}>{poStatusLabel(s)}</option>)}
            </select>
            <span className="inline-flex items-center text-xs text-slate-400 whitespace-nowrap">{data?.total ?? 0} orders</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">PO Number</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Supplier</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Order date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Expected</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Progress</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Total</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data.pos ?? []).length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-400">No purchase orders match your search or filter.</td>
                  </tr>
                )}
                {data.pos.map((po) => {
                  const items = itemsByPo[po.id] ?? [];
                  const progress = computePoProgress(items);
                  const isOpen = expanded === po.id;
                  return (
                    <Fragment key={po.id}>
                      <tr className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => setExpanded(isOpen ? null : po.id)}>
                        <td className="px-4 py-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/purchase-orders/${po.id}`); }}
                            className="text-sm font-semibold text-slate-800 font-mono hover:text-emerald-700 whitespace-nowrap"
                          >
                            {po.po_number}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{po.suppliers?.name ?? "—"}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{fmtDate(po.order_date)}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{fmtDate(po.expected_date)}</td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-700 whitespace-nowrap">
                            {progress.received} / {progress.ordered} received
                          </span>
                          {progress.outstanding > 0 ? (
                            <span className="block text-xs font-medium text-amber-600 mt-0.5">{progress.outstanding} outstanding</span>
                          ) : (
                            <span className="block text-xs font-medium text-emerald-600 mt-0.5">Complete</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-800 text-right font-medium whitespace-nowrap">{money(po.total)}</td>
                        <td className="px-4 py-3"><StatusBadge status={po.status} customLabel={poStatusLabel(po.status)} /></td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {canReceivePo(po.status) && canReceivePurchaseOrders(role) && (
                              <button
                                onClick={(e) => { e.stopPropagation(); openReceive(po); }}
                                className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
                              >
                                <i className="ri-download-2-line" />
                                Receive
                              </button>
                            )}
                            {canApprovePo(po.status) && canManagePurchaseOrders(role) && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleApprove(po); }}
                                className="px-3 py-1.5 bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
                              >
                                <i className="ri-check-line" />
                                Mark as ordered
                              </button>
                            )}
                            {canCancelPo(po.status) && canManagePurchaseOrders(role) && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setCancelPo(po); }}
                                className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500"
                                title="Cancel"
                              >
                                <i className="ri-close-circle-line" />
                              </button>
                            )}
                            <i className={`ri-arrow-down-s-line text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-slate-50 bg-slate-50/40">
                          <td colSpan={8} className="px-4 py-3">
                            <div className="divide-y divide-slate-100">
                              {items.map((it) => (
                                <div key={it.id} className="py-2 flex items-center justify-between text-sm">
                                  <div>
                                    <span className="font-medium text-slate-800">{historicalProductName(it)}</span>
                                    <span className="text-slate-400 font-mono text-xs ml-2">{historicalSku(it)}</span>
                                    {(variantLabel(historicalVariantOptions(it)) || historicalSku(it)) && (
                                      <span className="text-slate-500 text-xs ml-2">{variantLabel(historicalVariantOptions(it)) || historicalSku(it)}</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-6 text-slate-600">
                                    <span>{it.qty_received} / {it.qty_ordered} received</span>
                                    <span className="w-20 text-right">{money(it.unit_cost)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={data.total} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }} />
        </div>
      )}

      <ConfirmDialog
        open={!!cancelPo}
        title="Cancel Purchase Order"
        message={`Cancel ${cancelPo?.po_number ?? ""}? Any unreceived quantities will no longer count as incoming stock.`}
        confirmLabel="Cancel Order"
        loading={saving}
        onCancel={() => setCancelPo(null)}
        onConfirm={handleCancelPo}
      />

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="New Purchase Order" wide
        footer={
          <>
            <button onClick={() => setCreateOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleCreate} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Creating..." : "Create Order"}
            </button>
          </>
        }
      >
        <form onSubmit={handleCreate} className="space-y-4">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Supplier *</label>
              <select required value={supplierId} onChange={(e) => handleSelectSupplier(e.target.value)} className={inputCls}>
                <option value="">Select supplier...</option>
                {data?.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Receiving location *</label>
              <select required value={locationId} onChange={(e) => setLocationId(e.target.value)} className={inputCls}>
                <option value="">Select location...</option>
                {data?.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Expected date</label>
              <input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Supplier reference</label>
              <input type="text" value={supplierReference} onChange={(e) => setSupplierReference(e.target.value)} className={inputCls} placeholder="Optional" />
            </div>
            <div>
              <label className={labelCls}>Shipping cost</label>
              <input type="number" min="0" step="0.01" value={shippingCost} onChange={(e) => setShippingCost(e.target.value)} className={inputCls} placeholder="0.00" />
            </div>
            <div>
              <label className={labelCls}>Discount</label>
              <input type="number" min="0" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} className={inputCls} placeholder="0.00" />
            </div>
          </div>

          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500} className={inputCls} placeholder="Optional internal note" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-slate-500">Line items</label>
              <button type="button" onClick={addLine} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap">
                <i className="ri-add-line" /> Add line
              </button>
            </div>
            {lines.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">
                No items yet. Click "Add line" to get started.
              </div>
            ) : (
              <div className="space-y-2">
                {lines.map((l, idx) => {
                  const lineVariants = (data?.variants ?? []).filter((v) => v.product_id === l.product_id);
                  const ruleHint = orderRuleHint(supplierId, l.product_id);
                  const lineCurrency = resolveLineCurrency(supplierId, l.product_id);
                  const currencyMismatch = !!poCurrency && !!lineCurrency && currenciesDiffer(lineCurrency, poCurrency);
                  return (
                  <div key={l.uid}>
                  <div className="flex items-center gap-2">
                    <select
                      value={l.product_id}
                      onChange={(e) => handleSelectProduct(idx, e.target.value)}
                      className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                    >
                      <option value="">Select product...</option>
                      {data?.products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                    </select>
                    {lineVariants.length === 1 ? (
                      <span className="w-32 px-3 py-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg truncate whitespace-nowrap">
                        {variantLabel(lineVariants[0].option_values) || lineVariants[0].sku || "Default"}
                      </span>
                    ) : lineVariants.length > 1 ? (
                      <select
                        value={l.variant_id ?? ""}
                        onChange={(e) => {
                          const variantId = e.target.value || null;
                          updateLine(idx, { variant_id: variantId, unit_cost: String(resolveCost(l.product_id, variantId)) });
                        }}
                        className="w-32 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                      >
                        <option value="">Select variant…</option>
                        {lineVariants.map((v) => <option key={v.id} value={v.id}>{variantLabel(v.option_values) || v.sku}</option>)}
                      </select>
                    ) : null}
                    <input type="number" min="1" value={l.qty} onChange={(e) => updateLine(idx, { qty: Number(e.target.value) })} className="w-20 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" placeholder="Qty" />
                    <input type="number" min="0" step="0.01" value={l.unit_cost} onChange={(e) => updateLine(idx, { unit_cost: e.target.value })} className="w-24 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" placeholder="Cost" />
                    <input type="text" value={l.supplier_sku} onChange={(e) => updateLine(idx, { supplier_sku: e.target.value })} className="w-28 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" placeholder="Supplier SKU" />
                    <button type="button" onClick={() => removeLine(idx)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500">
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                  {ruleHint && <div className="mt-1 text-xs text-amber-600">{ruleHint}</div>}
                  {currencyMismatch && (
                    <div className="mt-1 text-xs text-red-600">Supplier price is in {lineCurrency?.toUpperCase()} but this order is in {poCurrency.toUpperCase()}. Resolve the currency mismatch.</div>
                  )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end text-sm gap-4">
            {poCurrency && <span className="text-xs text-slate-400">Currency: {poCurrency.toUpperCase()}</span>}
            <span className="text-slate-500 mr-2">Total:</span>
            <span className="font-semibold text-slate-900">{money(createTotal, poCurrency)}</span>
          </div>
        </form>
      </Modal>

      <Modal open={!!receivePo} onClose={() => setReceivePo(null)} title={`Receive — ${receivePo?.po_number ?? ""}`}
        footer={
          <>
            <button onClick={() => setReceivePo(null)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleReceive} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Receiving..." : "Confirm Receipt"}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          {(itemsByPo[receivePo?.id ?? ""] ?? []).map((it) => {
            const remaining = it.qty_ordered - it.qty_received;
            return (
              <div key={it.id} className="flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">{historicalProductName(it)}</p>
                  <p className="text-xs text-slate-400">
                    {(variantLabel(historicalVariantOptions(it)) || historicalSku(it)) ? `${variantLabel(historicalVariantOptions(it)) || historicalSku(it)} · ` : ""}
                    {it.qty_received} of {it.qty_ordered} received · {remaining} remaining
                  </p>
                </div>
                <input
                  type="number"
                  min="0"
                  max={remaining}
                  disabled={remaining <= 0}
                  value={receiveQty[it.id] ?? 0}
                  onChange={(e) => setReceiveQty((q) => ({ ...q, [it.id]: Number(e.target.value) }))}
                  className="w-24 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm disabled:opacity-50"
                />
              </div>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}